import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { LabelProps } from 'recharts';

/** Cartesian view box shape used by bar charts */
interface CartesianViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Type guard to check if viewBox is a Cartesian viewBox (not Polar) */
const isCartesianViewBox = (viewBox: unknown): viewBox is CartesianViewBox => {
  return typeof viewBox === 'object' && viewBox !== null && 'x' in viewBox && 'y' in viewBox && 'width' in viewBox && 'height' in viewBox;
};

/**
 * Custom Y-axis label component that truncates long labels.
 * Uses CSS text-overflow ellipsis with rotation to handle long labels gracefully.
 * Automatically adjusts position based on RTL/LTR direction.
 */
export const TruncatedYAxisLabel = (props: LabelProps) => {
  const { value, viewBox } = props;
  if (!isCartesianViewBox(viewBox) || value === undefined || value === null) return null;

  const labelHeight = 16;
  const maxHeight = viewBox.height - 20;
  const labelValue = String(value);

  // In RTL mode, position label to the right of the axis (which is now on the right side)
  const xPosition = viewBox.x - labelHeight;

  return (
    <foreignObject x={xPosition} y={viewBox.y} width={labelHeight} height={viewBox.height}>
      <div className="flex items-center justify-center h-full">
        <Text
          variant="caption-1"
          className={cn('truncate text-bar-chart-axis-label', '[writing-mode:vertical-rl] rotate-180')}
          title={labelValue}
          style={{
            maxHeight,
          }}
        >
          {labelValue}
        </Text>
      </div>
    </foreignObject>
  );
};
